/**
 * apps/web/src/pages/AuditViewerPage.tsx
 * -------------------------------------------------------
 * Purpose:
 *   Detailed audit inspection UI (job data + AI output).
 *
 * High-level data flow:
 *   - loader (router.tsx -> auditLoader) fetches `/api/audits/:auditId`.
 *   - The right column includes local manual-review controls backed by the app DB.
 */

import React, { useMemo } from 'react';
import {
  Button,
  Card,
  Grid,
  Group,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Link, useLoaderData, useSearchParams } from 'react-router-dom';

import type { AuditDetail } from '@tinypro-audit/contracts';
import { formatDateTimeInAppTimeZone } from '@tinypro-audit/shared/time';
import { EstimatesPanel } from '../components/EstimatesPanel';
import { AuditReviewStateCard } from '../components/AuditReviewStateCard';
import { JobSummaryCard } from '../components/JobSummaryCard';
import { SubmittedFormsViewer } from '../components/SubmittedFormsViewer';
import { AuditStatusCell } from '../components/AuditResolutionBadge';
import { inboxReturnPath } from './review-navigation';

/** Loader data returned by auditLoader(). */
type LoaderData = { detail: AuditDetail };

export function AuditViewerPage(): React.ReactElement {
  /** Audit detail returned by the route loader. */
  const { detail } = useLoaderData() as LoaderData;
  const [params] = useSearchParams();
  const returnTo = inboxReturnPath(params);
  const projectId = params.get('project');
  const projectPath =
    projectId && /^[1-9]\d{0,31}$/.test(projectId)
      ? `/projects/${projectId}`
      : null;

  /** Human-friendly timestamp for display in the app timezone. */
  const submittedAt = useMemo(
    () => formatDateTimeInAppTimeZone(detail.submittedAt),
    [detail.submittedAt],
  );

  /** Scalar metadata entries rendered in the generic details table. */
  const metadataEntries = useMemo(
    () => Object.entries(detail.formContent),
    [detail.formContent],
  );

  /** Tenant-specific ServiceTitan link is built by the API. */
  const serviceTitanUrl = detail.externalLinks.serviceTitanJobUrl;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Group align="flex-start">
          <Button
            component={Link}
            to={projectPath ?? returnTo ?? '/projects/pre-site'}
            variant="subtle"
            leftSection={<ArrowLeft size={16} />}
          >
            {projectPath
              ? 'Back to project'
              : returnTo
                ? 'Back to Inbox'
                : 'Pre-site form reviews'}
          </Button>
          <div>
            <Title order={3}>{detail.jobId}</Title>
            <Text c="dimmed">
              {detail.technicianName} • {detail.location} • Submitted{' '}
              {submittedAt}
            </Text>
            {projectPath && (
              <Text size="sm" c="dimmed">
                Latest stored pre-site report · Linked from project {projectId}
              </Text>
            )}
          </div>
        </Group>

        <Group>
          <Button
            component="a"
            href={serviceTitanUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="default"
            leftSection={<ExternalLink size={16} />}
          >
            Open in ServiceTitan
          </Button>
          <AuditStatusCell
            status={detail.status}
            reviewState={detail.reviewState}
          />
        </Group>
      </Group>

      <Grid>
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Stack gap="md">
            <JobSummaryCard summary={detail.jobSummary} />
            <EstimatesPanel estimates={detail.estimates} />
            <SubmittedFormsViewer forms={detail.submittedForms} />

            <Card withBorder radius="md">
              <Title order={4} mb="sm">
                Job metadata
              </Title>

              <Table>
                <Table.Tbody>
                  {metadataEntries.map(([key, value]) => (
                    <Table.Tr key={key}>
                      <Table.Td style={{ width: 220 }}>
                        <Text fw={600} size="sm">
                          {key}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          size="sm"
                          style={{
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {value === null || value === undefined
                            ? '—'
                            : String(value)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}

                  {metadataEntries.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={2}>
                        <Text c="dimmed" size="sm">
                          No additional metadata available.
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                </Table.Tbody>
              </Table>
            </Card>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Stack gap="md">
            {detail.status === 'Review' ? (
              <AuditReviewStateCard
                auditId={detail.id}
                initialReviewState={detail.reviewState}
              />
            ) : null}

            <Card withBorder radius="md">
              <Title order={4} mb="sm">
                Model output
              </Title>

              <div style={{ overflowWrap: 'anywhere' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {detail.auditOutputBase}
                </ReactMarkdown>
              </div>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
